import type { BlueprintWithTasks } from '../../types/server/models/blueprint'
import { Box, Button, Container, SlideFade, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react'
import { useCompanyBlueprints, useUserBlueprints, DeleteBlueprint } from '../../services/blueprint.service'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useReadCompany } from '../../services/company-service'
import { CirclePlusIcon } from '../../assets/CirclePlusIcon'
import { EditTaskIcon } from '../../assets/EditTaskIcon'
import { useUser } from '../../services/user-service'
import { TrashIcon } from '../../assets/TrashIcon'
import { useTranslation } from 'react-i18next'
// import TasklistService from '../../services/tasklist-service'
import style from '../../assets/style.json'
import Swal from 'sweetalert2'

const getEndDate = (blueprint: BlueprintWithTasks) =>
	blueprint.tasks.reduce((acc, curr) => {
		const endDate = curr.endDate ? new Date(curr.endDate) : new Date(0)
		return endDate > acc ? endDate : acc
	}, new Date(0))

const Blueprints = () => {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	const isCompanySelected = searchParams.get('company_id')

	const { data: user } = useUser()
	const { data: company } = useReadCompany(isCompanySelected || user?.company.id)

	const {
		data: userBlueprints,
		isLoading: isLoadingUserBlueprints,
		mutate: mutateUserBlueprints,
	} = isCompanySelected ? { data: [] as BlueprintWithTasks[], mutate: () => {} } : useUserBlueprints()

	const {
		data: companyBlueprints,
		isLoading: isLoadingCompanyBlueprints,
		mutate: mutateCompanyBlueprints,
	} = useCompanyBlueprints(isCompanySelected && company ? company._id : undefined)

	const blueprints = isCompanySelected ? companyBlueprints : userBlueprints
	const isLoading = isCompanySelected ? isLoadingCompanyBlueprints : isLoadingUserBlueprints
	const mutate = isCompanySelected ? mutateCompanyBlueprints : mutateUserBlueprints

	const notUsedRoles = company?.roles
		// Filter out roles that exists in any of the current blueprints
		.filter((r) => {
			return !companyBlueprints.some((b) => b.role === r._id)
		})
	console.log('notUsedRoles', notUsedRoles)
	const isCompanyHasMoreRoles = notUsedRoles && notUsedRoles.length > 0

	return (
		<Container
			maxW='full'
			minH='100vh'
			py={20}
			position='absolute'
			top={'90px'}
			centerContent
		>
			<SlideFade in>
				<Text
					color={'white'}
					dir='rtl'
					marginBottom={'77px'}
					fontWeight={500}
					fontSize={32}
				>
					{t('tasklists.lists')}
				</Text>
			</SlideFade>
			<Box
				overflowX='auto'
				w='90vw'
				maxW={style['pc-view']}
				boxShadow='0px 0px 25px rgba(62, 62, 62, 0.08)'
				bg='#fff'
				p={'20px'}
				rounded='10px'
			>
				{isLoading ? (
					<Box
						display='flex'
						minH={500}
						alignItems='center'
						justifyContent='center'
					>
						<div className='lds-default'>
							<div />
							<div />
							<div />
							<div />
							<div />
							<div />
							<div />
							<div />
							<div />
							<div />
							<div />
							<div />
						</div>
					</Box>
				) : (
					<>
						{!!isCompanySelected && (
							<Button
								onClick={() => navigate(`/admin/${isCompanySelected}/blueprints/add`)}
								minW={'188px'}
								textAlign='center'
								fontSize='18px'
								fontWeight={700}
								rounded={'100px'}
								borderWidth={'1.5px'}
								borderColor={style['primary-color']}
								bg={'#fff'}
								color={style['primary-color']}
								marginBottom='30px'
								isDisabled={!isCompanyHasMoreRoles}
							>
								<Box
									gap={'17px'}
									display={'flex'}
									alignItems={'center'}
								>
									<Text>{isCompanyHasMoreRoles ? t('tasklists.addList') : t('addTasksForm.addListDisabled')}</Text>
									<CirclePlusIcon />
								</Box>
							</Button>
						)}

						<Text
							fontSize={'18px'}
							fontWeight={400}
							color='#475154'
							textAlign={'center'}
							marginBottom='60px'
						>
							{company?.name}
						</Text>
						<Box overflowX={'auto'}>
							<SlideFade in>
								<Table>
									<Thead borderBottom={'1.5px solid #DFDFDF'}>
										<Tr>
											<Th
												fontSize={'16px'}
												fontWeight={700}
												lineHeight={'29px'}
												paddingBottom='12px'
												textAlign='start'
												position='sticky'
												right={'0'}
												zIndex={5}
												bg={'#fff'}
											>
												{t('tasklists.trustee')}
											</Th>

											<Th
												fontSize={'16px'}
												fontWeight={700}
												lineHeight={'29px'}
												paddingBottom='12px'
												textAlign='start'
											>
												{t('tasklists.employee')}
											</Th>
											<Th
												fontSize={'16px'}
												fontWeight={700}
												lineHeight={'29px'}
												paddingBottom='12px'
												textAlign='start'
											>
												{t('tasklists.endDate')}
											</Th>
											<Th
												fontSize={'16px'}
												fontWeight={700}
												lineHeight={'29px'}
												paddingBottom='12px'
												textAlign='start'
											>
												{t('tasklists.tasks')}
											</Th>
											<Th />
										</Tr>
									</Thead>
									<Tbody>
										{blueprints.map((blueprint, i) => {
											const roleId = blueprint.role as string
											const role = company?.roles?.find((r) => r._id === roleId)
											const roleText = role?.name || t('tasklists.unassigned')

											const employees =
												company?.employees
													.filter((e) => e.isActive)
													.filter((employee) =>
														employee.roleIds?.includes(role?.id || roleId)
													) || []

											const employeeText = employees.map((employee) => employee.name).join(', ')

											return (
												<Tr
													key={blueprint._id}
													rounded={'10px'}
													border={'none'}
													position='relative'
												>
													<Td
														onClick={() =>
															navigate(
																`/admin/${isCompanySelected}/blueprints/${blueprint._id}/edit`
															)
														}
														cursor='pointer'
														color='#3FB2D2'
														roundedRight={'10px'}
														bg={i % 2 === 0 ? 'white' : '#F5F8FA'}
														border={'none'}
														textAlign='start'
														position='sticky'
														right={'0'}
														zIndex={5}
													>
														{roleText}
													</Td>
													<Td
														bg={i % 2 === 0 ? 'white' : '#F5F8FA'}
														border={'none'}
														textAlign='start'
													>
														{employeeText}
													</Td>
													<Td
														bg={i % 2 === 0 ? 'white' : '#F5F8FA'}
														border={'none'}
														textAlign='start'
													>
														{blueprint.tasks.length > 0
															? getEndDate(blueprint).toLocaleDateString()
															: ''}
													</Td>
													<Td
														bg={i % 2 === 0 ? 'white' : '#F5F8FA'}
														border={'none'}
														textAlign='start'
													>
														X/{blueprint.tasks.length}
														{!isCompanySelected && (
															<Text
																fontSize={10}
																color={
																	0 === (blueprint.tasks.length || 0)
																		? '#3FB2D2'
																		: '#E55353'
																}
															>
																{0 === blueprint.tasks.length || 0
																	? 'Complete'
																	: 'Incomplete'}
															</Text>
														)}
													</Td>

													<Td
														bg={i % 2 === 0 ? 'white' : '#F5F8FA'}
														border={'none'}
														textAlign='start'
														roundedLeft={'10px'}
														width='150px'
														minWidth='150px'
													>
														{!!isCompanySelected && (
															<>
																<Button
																	color={'#3FB2D2'}
																	bg='transparent'
																	onClick={() =>
																		navigate(
																			`/admin/${isCompanySelected}/blueprints/${blueprint._id}/edit`
																		)
																	}
																>
																	<EditTaskIcon />
																</Button>
																<Button
																	color={'#3FB2D2'}
																	bg='transparent'
																	onClick={async () => {
																		const { isConfirmed } = await Swal.fire({
																			title: `${t(
																				'tasklists.deleteTasksList'
																			)}: ${roleText}?`,
																			text: t('tasklists.deleteWarning'),
																			showCancelButton: true,
																			confirmButtonColor: '#3085d6',
																			cancelButtonColor: '#d33',
																			cancelButtonText: t('tasklists.cancel'),
																			confirmButtonText: t('tasklists.delete'),
																		})

																		if (isConfirmed) {
																			await DeleteBlueprint(blueprint._id)
																			await mutate()
																		}
																	}}
																>
																	<TrashIcon />
																</Button>
															</>
														)}
													</Td>
												</Tr>
											)
										})}
									</Tbody>
								</Table>
							</SlideFade>
						</Box>
					</>
				)}
			</Box>
		</Container>
	)
}

export default Blueprints
